import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Cluster} from '@dropbox/dig-foundations';
import {EmptyFileCabinetSpot, PeacefulHammockSpot, TravelVanSpot} from '@dropbox/dig-illustrations';
import {derivedThemeAtom} from 'atoms/layout';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';

import styles from './NotificationEmptyState.module.css';

const NotificationEmptyInbox = () => {
  const isDarkMode = useAtomValue(derivedThemeAtom) === 'dark';
  return (
    <Cluster
      style={{width: 200, height: 200}}
      className={atoms({paddingTop: '20', paddingBottom: '10', position: 'relative'})}
    >
      <Cluster.Item style={{width: 186, height: 186}} className={atoms({position: 'absolute'})}>
        <Box backgroundColor="Background Subtle" borderRadius="Circular" height="100%" />
      </Cluster.Item>
      <Cluster.Item
        className={isDarkMode ? styles.hammockDark : styles.hammock}
        style={{position: 'absolute'}}
      >
        <PeacefulHammockSpot style={{width: 200}} />
      </Cluster.Item>
    </Cluster>
  );
};

const NotificationEmptyUpcoming = () => {
  const isDarkMode = useAtomValue(derivedThemeAtom) === 'dark';
  return (
    <Cluster
      style={{width: 282, height: 200}}
      className={atoms({paddingTop: '24', paddingBottom: '10', position: 'relative'})}
    >
      <Cluster.Item
        style={{width: 282, height: 67, bottom: 30}}
        className={atoms({position: 'absolute'})}
      >
        <Box
          tagName="div"
          borderRadius="Circular"
          height="100%"
          className={isDarkMode ? styles.upcomingDark : styles.upcoming}
          style={{borderBottomLeftRadius: 0, borderBottomRightRadius: 0}}
        />
      </Cluster.Item>

      <Cluster.Item className={atoms({position: 'absolute'})}>
        <TravelVanSpot style={{width: 282, height: 200}} />
      </Cluster.Item>
    </Cluster>
  );
};

const NotificationEmptyArchive = () => {
  const isDarkMode = useAtomValue(derivedThemeAtom) === 'dark';
  return (
    <Cluster
      style={{width: 244, height: 160}}
      className={atoms({paddingTop: '28', paddingBottom: '10', position: 'relative'})}
    >
      <Cluster.Item style={{width: 244, height: 120}} className={atoms({position: 'absolute'})}>
        <Box backgroundColor="Background Subtle" borderRadius="Medium" height="100%" />
      </Cluster.Item>
      <Cluster.Item
        className={isDarkMode ? styles.cabinetDark : styles.cabinet}
        style={{position: 'absolute'}}
      >
        <EmptyFileCabinetSpot style={{width: 244, height: 160}} />
      </Cluster.Item>
    </Cluster>
  );
};

const emptyStates = {
  inbox: {
    Image: NotificationEmptyInbox,
    title: 'notification_inbox_empty_title',
    body: 'notification_inbox_empty_body',
  },
  upcoming: {
    Image: NotificationEmptyUpcoming,
    title: 'notification_upcoming_empty_title',
    body: 'notification_upcoming_empty_body',
  },
  archive: {
    Image: NotificationEmptyArchive,
    title: 'notification_archive_empty_title',
    body: 'notification_archive_empty_body',
  },
};

export const NotificationEmptyState = ({type}: {type: 'inbox' | 'archive' | 'upcoming'}) => {
  const {Image, title, body} = emptyStates[type];
  return (
    <Box
      width="100%"
      alignItems="center"
      textAlign="center"
      flexDirection="column"
      display="flex"
      justifyContent="center"
      paddingX="68"
    >
      <Image />
      <Text size="large" isBold variant="paragraph">
        {t(title)}
      </Text>
      <Text color="faint" variant="paragraph">
        {t(body)}
      </Text>
    </Box>
  );
};
